// PreviewTransferAlert.jsx
import React from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

/**
 * showPreviewTransferAlert
 */
export function showPreviewTransferAlert({
  retailers = [],
  distributor = { orgCode: "", orgName: "", distName: "", role: "" },
  onConfirm,
  onCancel,
  title = "Preview Response",
}) {
  return MySwal.fire({
    html: (
      <div className="min-w-10">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 p-4 md:p-5 border-b border-slate-200 bg-white">
          <h2 className="text-lg md:text-xl font-semibold text-slate-800">{title}</h2>
          <span className="text-xs md:text-sm text-slate-500">Transfer review</span>
        </div>

        {/* Body */}
        <div
          className="p-4 md:p-6 bg-white overflow-y-auto"
          style={{ maxHeight: "60vh" }} // ✅ Limit modal body height
        >
          {/* Transfer (Retailers) */}
          <section className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
                Transfer
              </span>
              <span className="text-xs md:text-sm text-slate-500">Retailers (multiple)</span>
            </div>

            <div className="border border-slate-200 rounded-2xl overflow-hidden">
              <div className="h-[25vh] overflow-auto divide-y divide-slate-100">
                {retailers.length === 0 && (
                  <div className="p-4 text-sm text-slate-500">No retailers selected.</div>
                )}

                {retailers.map((r, idx) => (
                  <div
                    key={`${r.orgCode}-${idx}`}
                    className="p-4 flex items-center justify-between gap-3 bg-white hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <p className="text-sm md:text-base font-medium text-slate-800 truncate">
                        {r.orgName}
                      </p>
                      <p className="text-xs text-slate-500 truncate">Org Code: {r.orgCode}</p>
                    </div>
                    <span className="text-[10px] md:text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700">
                      {r.role}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Transfer To (Distributor) */}
          {/* Transfer To (Distributor) */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium bg-red-50 text-red-700 ring-1 ring-inset ring-red-200">
                Transfer to
              </span>
              <span className="text-xs md:text-sm text-slate-500">{distributor.role}</span>
            </div>

            <div className="border border-slate-200 rounded-2xl overflow-hidden bg-white">
              <div className="p-4 md:p-5 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm md:text-base font-semibold text-slate-800">
                    {distributor.distName || distributor.orgName || "—"}
                  </p>
                  <p className="text-xs text-slate-500">
                    Org: {distributor.orgName || "—"} • Code: {distributor.orgCode || "—"}
                  </p>
                </div>
                <span className="text-[10px] md:text-xs px-2 py-1 rounded-full bg-blue-600 text-white">
                  {distributor.role || "—"}
                </span>
              </div>
            </div>
          </section>

        </div>
      </div>
    ),

    // Buttons
    showCancelButton: true,
    confirmButtonText: "Confirm Transfer",
    cancelButtonText: "Cancel",

    customClass: {
      popup:
        "!p-0 !rounded-2xl !overflow-hidden !bg-white !shadow-2xl !border !border-slate-200 !max-w-[700px] !w-[90vw]", // ✅ Wider modal (700px)
      htmlContainer: "!m-0",
      confirmButton:
        "!bg-blue-600 hover:!bg-blue-700 !text-white !font-medium !rounded-xl !px-5 !py-2.5 !mt-0",
      cancelButton:
        "!bg-red-600 hover:!bg-red-700 !text-white !font-medium !rounded-xl !px-5 !py-2.5 !mt-0 !ml-2",
      actions: "!p-4 md:!p-5 !bg-white !border-t !border-slate-200",
    },

    // Behavior
    reverseButtons: false,
    focusConfirm: true,
    allowOutsideClick: false,
    allowEscapeKey: true,
    width: "700px", // ✅ also define via property for clarity
    heightAuto: false,
  }).then((result) => {
    if (result.isConfirmed) {
      onConfirm?.();
    } else {
      onCancel?.();
    }
  });
}

export default function TransferPreviewButton() {
  const retailers = [
    { orgCode: "R001", orgName: "Apex Retail Mart" },
    { orgCode: "R002", orgName: "City Corner Store" },
    { orgCode: "R003", orgName: "GreenLeaf Retailers" },
    { orgCode: "R004", orgName: "Tech Bazaar" },
    { orgCode: "R005", orgName: "Metro Sales" },
    { orgCode: "R006", orgName: "Urban Store" },
  ];

  const distributor = {
    distName: "North Star Distributors",
    orgName: "North Star Org",
    orgCode: "D-1007",
  };

  const openPreview = () => {
    showPreviewTransferAlert({
      retailers,
      distributor,
      onConfirm: () => console.log("Confirmed!"),
      onCancel: () => console.log("Cancelled!"),
    });
  };

  return (
    <button
      onClick={openPreview}
      className="inline-flex items-center rounded-2xl px-5 py-3 bg-blue-600 text-white hover:bg-blue-700 shadow-md transition focus:outline-none focus:ring-2 focus:ring-blue-400"
    >
      Preview Transfer
    </button>
  );
}
